// client/src/components/profile/connectedApps/ConnectedAppsTab.js
import { useEffect, useRef, useState } from 'react';
import useConnectedApps from './useConnectedApps';
import DisconnectAppDialog from './DisconnectAppDialog';
import AppGroups from './AppGroups';
import NexusCard from './NexusCard';
import AppFilters from './AppFilters';
import { appLabel, duplicateSuffixes, filterApps, filterByPill, isAppKind, isUnnamed, nexusApp, visibleApps, pillCounts, summaryLine, withSuffix } from './groupApps';
import { REFRESH_ERRORS, disconnectErrors, stateSentence } from './copy';
import { toastSuccess, toastError, toastInfo, errorMessage } from '../../../lib/toast';
import { formatDateTime } from '../../../lib/formatDate';
import useItAppItems from './useItAppItems';
import ItAppItemRow from './ItAppItemRow';
import { LeaverBanner, ItChecklistError, itGroupDefs } from './ItAppItemGroups';
import { combinedProgress, filterItItems, itEffectiveStatus, itemLabel, itRowId, splitItItems } from './checklist';
import './connectedApps.css';

const Skeleton = () => (
  <ul className="ca-list" aria-hidden="true">{[0, 1, 2].map((i) => <li key={i} className="ca-row ca-row--skeleton" />)}</ul>
);

// itAppsEnabled: the it_apps flag, passed by ProfilePage (off by default so the
// Google-only tab is unchanged wherever the flag isn't wired).
export default function ConnectedAppsTab({ userId, isMemberView = false, canManage = false, subjectFirstName = '', itAppsEnabled = false }) {
  const { status, data, reload, refresh, setClosed, disconnect } = useConnectedApps({ userId, isMemberView });
  const it = useItAppItems({ userId, enabled: isMemberView && itAppsEnabled });
  const [refreshing, setRefreshing] = useState(false);
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState('');
  const [pill, setPill] = useState('all');
  const [autoDone, setAutoDone] = useState(false);
  // Odoo checks in flight, and rows Odoo said are still active (keyed by row id).
  const [verifying, setVerifying] = useState(() => new Set());
  const [stillActive, setStillActive] = useState(() => new Set());
  const manage = isMemberView && canManage;
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const opener = useRef(null);
  // Suffixes for repeated names, computed over every visible row (not the
  // filtered list) so "Robusta (2)" keeps its number while searching.
  const suffixes = status === 'ready' && data?.apps ? duplicateSuffixes(data.apps) : {};
  const labelOf = (app) => withSuffix(appLabel(app), suffixes[app.id]);
  const disclosureRefs = useRef({});
  const headingRef = useRef(null);
  const autoRan = useRef(false);

  // Auto-check (spec 4a): Nexus has never attempted a check, and the viewer is
  // the person or may refresh. One attempt per mount; the ref (not state) is the
  // guard so a failed attempt can never loop. A view-only holder never causes a
  // Google call by looking.
  const eligible = !isMemberView || manage;
  const needsAutoCheck = status === 'ready' && !!data.configured && data.sync === null && eligible && !autoDone;
  useEffect(() => {
    if (!needsAutoCheck || autoRan.current) return;
    autoRan.current = true;
    (async () => {
      try { await refresh(); }
      // The server stamps a failed attempt, so a reload shows the real failed
      // state. No toast: the person did not ask for this check.
      catch {
        // Done before the reload so there is no frame of "Checking…" after the failure.
        if (mounted.current) setAutoDone(true);
        await reload();
      }
      finally { if (mounted.current) setAutoDone(true); }
    })();
  }, [needsAutoCheck, refresh, reload]);

  const openDisconnect = (app) => { opener.current = document.activeElement; setPending(app); };
  // Cancel/Esc: hand focus back to the button that opened the dialog.
  const cancelDisconnect = () => {
    setPending(null);
    const el = opener.current;
    setTimeout(() => { if (el && el.isConnected) el.focus(); }, 0);
  };

  const onRefresh = async () => {
    setRefreshing(true);
    try {
      const before = data && data.sync && data.sync.last_success_at;
      const next = await refresh();
      if (!mounted.current) return;
      const sync = next && next.sync;
      const sentence = stateSentence(sync, isMemberView);
      // A 200 can still carry a failed check (e.g. the Google account is gone):
      // say that, never "refreshed".
      if (sentence) toastInfo(sentence);
      else if (sync && sync.last_success_at && (!before || new Date(sync.last_success_at) > new Date(before))) {
        toastSuccess('List refreshed from Google');
      }
    } catch (err) { toastError(errorMessage(err, "Couldn't reach Google. Try again later.", REFRESH_ERRORS)); }
    finally { if (mounted.current) setRefreshing(false); }
  };
  // A tick/undo moves the row to another group, unmounting the control that has
  // focus. Hand focus to the next row's checkbox, else the previous one, else
  // the group heading, else the tab heading, so keyboard users never land on <body>.
  const moveFocusFrom = (elId) => {
    const row = document.getElementById(elId)?.closest('li');
    const sib = row && (row.nextElementSibling || row.previousElementSibling);
    const sibId = sib?.querySelector('input[type="checkbox"]')?.id;
    const group = row?.closest('.ca-group')?.dataset.group;
    setTimeout(() => {
      const el = (sibId && document.getElementById(sibId))
        || (group && document.querySelector(`[data-group="${group}"] .ca-group__title, [data-group="${group}"] .ca-disclosure`))
        || headingRef.current;
      el?.focus();
    }, 0);
  };
  // One save per app at a time: a second toggle (e.g. Undo) racing the first
  // would be overwritten by the first one's rollback if that save failed.
  const togglesInFlight = useRef(new Set());
  const onToggle = async (app, closed) => {
    if (togglesInFlight.current.has(app.id)) return;
    togglesInFlight.current.add(app.id);
    const name = labelOf(app);
    try {
      const done = setClosed(app, closed);
      moveFocusFrom(`ca-app-${app.id}`);
      await done;
      toastSuccess(closed ? `Marked ${name} as closed` : `Marked ${name} as open again`);
    } catch { toastError("Couldn't save. Try again."); }
    finally { togglesInFlight.current.delete(app.id); }
  };
  // IT checklist tick / not applicable / undo: same one-save-per-item guard,
  // optimistic move with rollback, and a toast either way (spec §12.7).
  const onItSet = async (item, next) => {
    const key = `it:${item.app_id}:${item.item_type}`;
    if (togglesInFlight.current.has(key)) return;
    togglesInFlight.current.add(key);
    const name = itemLabel(item);
    try {
      const done = it.setItem(item, next);
      moveFocusFrom(itRowId(item));
      await done;
      if (next === 'closed') toastSuccess(`Marked ${name} closed`);
      else if (next === 'not_applicable') toastSuccess(`Marked ${name} as not applicable`);
      else toastSuccess(`Reopened ${name}`);
    } catch { toastError(`Couldn't update ${name}. Try again.`); }
    finally { togglesInFlight.current.delete(key); }
  };

  // Check in Odoo: not optimistic (the answer is Odoo's), so the row moves only
  // on success. Still active is an inline note on the row, not a toast.
  const toggleKey = (setter, key, on) => setter((prev) => {
    const next = new Set(prev);
    if (on) next.add(key); else next.delete(key);
    return next;
  });
  const onItVerify = async (item) => {
    const key = itRowId(item);
    if (togglesInFlight.current.has(key)) return;
    togglesInFlight.current.add(key);
    toggleKey(setVerifying, key, true);
    toggleKey(setStillActive, key, false);
    try {
      const verified = await it.verifyItem(item);
      moveFocusFrom(key);
      toastSuccess(verified === 'odoo_no_user'
        ? 'No Odoo user found for this person'
        : 'Confirmed the Odoo user is archived');
    } catch (err) {
      if (err?.response?.data?.error === 'odoo_user_active') {
        if (mounted.current) toggleKey(setStillActive, key, true);
      } else {
        toastError("Couldn't reach Odoo. Try again.");
      }
    } finally {
      togglesInFlight.current.delete(key);
      if (mounted.current) toggleKey(setVerifying, key, false);
    }
  };

  const onConfirmDisconnect = async () => {
    const name = labelOf(pending);
    setBusy(true);
    try {
      await disconnect(pending);
      toastSuccess(`Disconnected ${name}`);
      setPending(null);
      // The row (and its button) is gone: land on the Disconnected disclosure, else the heading.
      setTimeout(() => { (disclosureRefs.current.disconnected || headingRef.current)?.focus(); }, 0);
    } catch (err) {
      if (err?.response?.data?.error === 'already_disconnected') {
        // Lost a race with another tab: nothing to retry, show the truth.
        setPending(null);
        toastInfo(`${name} was already disconnected.`);
        // The reload moves the row (and the button that had focus) into the
        // Disconnected group: land there, as after a successful disconnect.
        await reload();
        if (mounted.current) {
          setTimeout(() => { (disclosureRefs.current.disconnected || headingRef.current)?.focus(); }, 0);
        }
        return;
      }
      toastError(errorMessage(err, `Couldn't disconnect ${name}. Try again.`, disconnectErrors(name)));
    } finally { if (mounted.current) setBusy(false); }
  };

  const loading = status === 'loading' || needsAutoCheck;
  const ready = status === 'ready' && !needsAutoCheck;
  const hasApps = ready && data.configured && !!data.sync?.last_success_at && data.apps.length > 0;
  // Devices and unnamed sign-ins are not IT's to-close list: counts use app-kind rows.
  const nexus = hasApps ? nexusApp(data.apps) : null;
  const listApps = hasApps ? visibleApps(data.apps) : [];
  const appKindApps = listApps.filter(isAppKind);
  const q = query.trim();
  const filtering = pill !== 'all';
  const shown = hasApps ? filterByPill(filterApps(listApps, q), pill) : [];
  // IT checklist. Google's side is "known" once a check has succeeded; until
  // then the counter and completion line stay hidden (never a partial total).
  const itStatus = isMemberView ? itEffectiveStatus(it.status, it.data) : 'off';
  const itReady = itStatus === 'ready';
  const googleKnown = ready && !!data.configured && !!data.sync?.last_success_at;
  const progress = combinedProgress({ googleAppKind: appKindApps, googleKnown, itStatus, itData: it.data });
  // Filter pills are Google attributes (signs in / has access): IT items have
  // neither, so a pill hides them. Search matches their name.
  const itSplit = itReady && !filtering ? splitItItems(it.data) : { closeManually: [], removeSeat: [], resolved: [] };
  const itGroups = itGroupDefs({ closeManually: filterItItems(itSplit.closeManually, q), removeSeat: filterItItems(itSplit.removeSeat, q) });
  const itClosed = filterItItems(itSplit.resolved, q);
  const itShownCount = itGroups.reduce((n, g) => n + g.items.length, 0) + itClosed.length;
  const renderItRow = (item) => (
    <ItAppItemRow key={itRowId(item)} item={item} canManage={manage} onSet={onItSet} onVerify={onItVerify}
      verifying={verifying.has(itRowId(item))} stillActive={stillActive.has(itRowId(item))} />
  );
  const itProps = itReady ? { itGroups, itClosed, renderItRow } : {};
  const allDone = isMemberView && !q && !filtering && progress.allDone;
  const doneLine = allDone && <p className="profile-muted">All accounts closed. Nothing left to do.</p>;
  // Outside the Google list state (failed, unconfigured, never checked, no
  // apps) the IT groups still render after Google's message.
  const itOnly = itReady && <AppGroups apps={[]} itView searching={!!q} rowProps={{}} disclosureRefs={disclosureRefs} {...itProps} />;
  const rowProps = { isMemberView, canManage: manage, subjectFirstName, onToggle, onDisconnect: openDisconnect };
  const note = ready && data.configured ? stateSentence(data.sync, isMemberView) : null;

  let body;
  if (loading) {
    body = (
      <>
        {needsAutoCheck && <p className="profile-muted" role="status">Checking with Google…</p>}
        <Skeleton />
      </>
    );
  } else if (status === 'error') {
    body = (
      <>
        <p className="ca-note ca-note--error">
          Couldn't load connected apps. <button type="button" className="btn btn-secondary btn-small" onClick={reload}>Try again</button>
        </p>
        {itOnly}
      </>
    );
  } else if (!data.configured) {
    body = <><p className="profile-muted">Google Workspace isn't connected to Nexus yet.</p>{itOnly}</>;
  } else if (!data.sync || !data.sync.last_success_at) {
    // Never succeeded: a recorded failure is not "not checked yet".
    body = <><p className={note ? 'ca-note' : 'profile-muted'}>{note || 'Not checked with Google yet.'}</p>{itOnly}</>;
  } else if (!data.apps.length) {
    body = (
      <>
        <p className="profile-muted">No apps are connected to this Google account.</p>
        {doneLine}
        {itOnly}
      </>
    );
  } else {
    body = (
      <>
        {note && <p className="ca-note">{note}</p>}
        {shown.length === 0 && itShownCount === 0 && filtering && <p className="profile-muted">No apps match these filters.</p>}
        {shown.length === 0 && itShownCount === 0 && !filtering && q && <p className="profile-muted">No apps match “{q}”.</p>}
        {doneLine}
        <AppGroups apps={shown} itView={isMemberView} searching={!!q || filtering} rowProps={rowProps} disclosureRefs={disclosureRefs} suffixes={suffixes} {...itProps} />
      </>
    );
  }

  const canRefresh = ready && data.configured && (!isMemberView || manage);
  return (
    <section className="profile-section ca" aria-labelledby="ca-title" aria-busy={loading}>
      <div className="ca-head">
        <h2 className="profile-section__title" id="ca-title" tabIndex={-1} ref={headingRef}>Connected apps</h2>
        {canRefresh && (
          <button type="button" className="btn btn-secondary btn-small" onClick={onRefresh} disabled={refreshing} aria-busy={refreshing}>
            {refreshing ? 'Refreshing…' : (isMemberView ? 'Refresh from Google' : 'Check again')}
          </button>
        )}
      </div>
      <p className="profile-muted">
        {/* Only a manage holder has the checkboxes; telling a view-only holder
            to tick would point at a control they don't have. */}
        {!isMemberView
          ? 'Apps you signed into with your Robusta Google account. IT uses this list to close your accounts when you leave.'
          : manage
            ? "Apps signed into with this person's Google account. Tick each one once the account inside the app is closed."
            : "Apps signed into with this person's Google account. IT ticks each one once the account inside the app is closed."}
      </p>
      {itReady && <LeaverBanner leaver={it.data.leaver} />}
      {isMemberView && it.status === 'error' && <ItChecklistError googleLoaded={status === 'ready'} />}
      {nexus && <NexusCard app={nexus} isMemberView={isMemberView} />}
      {/* No Google list to hang the counter on: give the combined counter its own line. */}
      {!hasApps && itReady && progress.showCounter && (
        <div className="ca-summary"><span className="ca-counter">{progress.done} of {progress.total} closed</span></div>
      )}
      {hasApps && (
        <div className="ca-summary">
          <span>{summaryLine(data.apps, { itView: isMemberView })}</span>
          {isMemberView && progress.showCounter && <span className="ca-counter">{progress.done} of {progress.total} closed</span>}
          <span className="ca-footer">Last checked with Google: {formatDateTime(data.sync.last_success_at)}</span>
          <div className="ca-search">
            <label htmlFor="ca-search" className="sr-only">Search connected apps</label>
            <input id="ca-search" type="search" className="search-input" value={query} placeholder="Search apps…" autoComplete="off"
              onChange={(e) => setQuery(e.target.value)} />
          </div>
          <AppFilters value={pill} counts={pillCounts(data.apps)} onChange={setPill} isMemberView={isMemberView} />
        </div>
      )}
      {body}
      {pending && (
        <DisconnectAppDialog app={{ ...pending, display_text: labelOf(pending), app_id: isUnnamed(pending.display_text) ? pending.display_text : null }} busy={busy} onConfirm={onConfirmDisconnect} onCancel={cancelDisconnect} />
      )}
    </section>
  );
}
